import Anthropic from '@anthropic-ai/sdk';

// Configuration
const API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;
const MODEL = process.env.REACT_APP_CLAUDE_MODEL || 'claude-3-sonnet-20240229';

// Initialize Anthropic client
let anthropic: Anthropic | null = null;

const initializeClient = () => {
  if (!API_KEY) {
    console.warn('Claude API key not configured. Using mock responses.');
    return null;
  }

  if (!anthropic) {
    anthropic = new Anthropic({
      apiKey: API_KEY,
      dangerouslyAllowBrowser: true // Note: In production, use a backend proxy
    });
  }

  return anthropic;
};

// Base Claude API call function
export const callClaude = async (prompt: string, systemPrompt?: string): Promise<string> => {
  const client = initializeClient();

  if (!client) {
    // Fallback to mock response when API key is not configured
    return getMockResponse(prompt);
  }

  try {
    const response = await client.messages.create({
      model: MODEL,
      max_tokens: 1024,
      messages: [
        {
          role: 'user',
          content: prompt
        }
      ],
      system: systemPrompt
    });

    return response.content[0].type === 'text' ? response.content[0].text : '';
  } catch (error) {
    console.error('Claude API error:', error);
    // Fallback to mock response on error
    return getMockResponse(prompt);
  }
};

// Mock response for when API is not available
const getMockResponse = (prompt: string): string => {
  if (prompt.toLowerCase().includes('research')) {
    return 'Mock research response: This is a simulated research opportunity recommendation.';
  }
  if (prompt.toLowerCase().includes('essay')) {
    return 'Mock essay response: This is a simulated essay analysis and feedback.';
  }
  if (prompt.toLowerCase().includes('college')) {
    return 'Mock college response: This is a simulated college recommendation.';
  }
  return 'Mock response: This is a simulated AI response.';
};

// Research Opportunities Service
export const generateResearchOpportunities = async (
  interests: string[],
  academicLevel: string,
  location?: string
): Promise<any[]> => {
  const prompt = `
Generate 5 research opportunities for a ${academicLevel} student interested in: ${interests.join(', ')}.
${location ? `Location preference: ${location}` : ''}

For each opportunity, provide:
1. Title
2. Institution/Organization
3. Brief description (2-3 sentences)
4. Duration
5. Application deadline
6. Requirements
7. Benefits

Format as JSON array with these fields: title, institution, description, duration, deadline, requirements, benefits, category, difficulty.
`;

  const systemPrompt = `You are an expert academic advisor specializing in research opportunities for students. Provide realistic, current research opportunities that match the student's interests and level.`;

  try {
    const response = await callClaude(prompt, systemPrompt);

    // Try to parse JSON response
    try {
      return JSON.parse(response);
    } catch {
      // If JSON parsing fails, return mock data
      return generateMockResearchOpportunities(interests, academicLevel);
    }
  } catch (error) {
    console.error('Research opportunities generation failed:', error);
    return generateMockResearchOpportunities(interests, academicLevel);
  }
};

// Essay Analysis Service
export const analyzeEssay = async (
  essayText: string,
  essayType: string = 'personal statement'
): Promise<any> => {
  const prompt = `
Analyze this ${essayType} essay and provide detailed feedback:

"${essayText}"

Provide analysis in the following areas:
1. Overall Score (1-10)
2. Strengths (3-5 points)
3. Areas for Improvement (3-5 points)
4. Specific Suggestions (3-5 actionable items)
5. Grammar and Style feedback
6. Content and Structure feedback

Format as JSON with fields: overallScore, strengths, improvements, suggestions, grammar, content.
`;

  const systemPrompt = `You are an expert college admissions counselor and essay reviewer. Provide constructive, specific feedback that helps students improve their essays.`;

  try {
    const response = await callClaude(prompt, systemPrompt);

    try {
      return JSON.parse(response);
    } catch {
      return generateMockEssayAnalysis(essayText);
    }
  } catch (error) {
    console.error('Essay analysis failed:', error);
    return generateMockEssayAnalysis(essayText);
  }
};

// College Matching Service
export const generateCollegeRecommendations = async (
  profile: {
    gpa: number;
    satScore?: number;
    actScore?: number;
    interests: string[];
    preferredMajors: string[];
    location?: string;
    size?: string;
    type?: string;
  }
): Promise<any[]> => {
  const prompt = `
Based on this student profile, recommend 10 colleges that would be good matches:

GPA: ${profile.gpa}
${profile.satScore ? `SAT Score: ${profile.satScore}` : ''}
${profile.actScore ? `ACT Score: ${profile.actScore}` : ''}
Interests: ${profile.interests.join(', ')}
Preferred Majors: ${profile.preferredMajors.join(', ')}
${profile.location ? `Location Preference: ${profile.location}` : ''}
${profile.size ? `School Size Preference: ${profile.size}` : ''}
${profile.type ? `School Type Preference: ${profile.type}` : ''}

For each college, provide:
1. Name
2. Location
3. Match type (safety/match/reach)
4. Acceptance rate
5. Average GPA of admitted students
6. Why it's a good fit (2-3 sentences)
7. Notable programs
8. Campus culture

Format as JSON array with fields: name, location, matchType, acceptanceRate, averageGPA, whyGoodFit, notablePrograms, culture, rank.
`;

  const systemPrompt = `You are an expert college counselor with deep knowledge of colleges and universities. Provide realistic recommendations based on the student's academic profile and preferences.`;

  try {
    const response = await callClaude(prompt, systemPrompt);

    try {
      return JSON.parse(response);
    } catch {
      return generateMockCollegeRecommendations(profile);
    }
  } catch (error) {
    console.error('College recommendations failed:', error);
    return generateMockCollegeRecommendations(profile);
  }
};

// Mock data generators (fallbacks)
const generateMockResearchOpportunities = (interests: string[], level: string) => [
  {
    title: `${interests[0]} Research Initiative`,
    institution: 'Local University',
    description: 'Mock research opportunity in your area of interest.',
    duration: '6-8 weeks',
    deadline: '2024-03-15',
    requirements: ['Strong academic record', 'Letter of recommendation'],
    benefits: ['Research experience', 'Mentorship', 'Certificate'],
    category: interests[0],
    difficulty: level === 'high school' ? 'Beginner' : 'Intermediate'
  }
];

const generateMockEssayAnalysis = (text: string) => ({
  overallScore: 7.5,
  strengths: [
    'Clear thesis statement',
    'Good personal examples',
    'Engaging opening'
  ],
  improvements: [
    'Strengthen conclusion',
    'Add more specific details',
    'Improve transitions'
  ],
  suggestions: [
    'Consider adding a compelling hook',
    'Expand on personal growth',
    'Proofread for minor errors'
  ],
  grammar: 'Generally good with minor issues',
  content: 'Strong personal narrative with room for more depth'
});

const generateMockCollegeRecommendations = (profile: any) => [
  {
    name: 'Sample University',
    location: 'Your State',
    matchType: 'match',
    acceptanceRate: '45%',
    averageGPA: profile.gpa + 0.2,
    whyGoodFit: 'Strong programs in your areas of interest with good academic fit.',
    notablePrograms: profile.preferredMajors,
    culture: 'Collaborative and academically focused',
    rank: 75
  }
];

// Utility function to check if API is configured
export const isClaudeConfigured = (): boolean => {
  return !!API_KEY;
};

export default {
  callClaude,
  generateResearchOpportunities,
  analyzeEssay,
  generateCollegeRecommendations,
  isClaudeConfigured
};